import {
  Component,
  Prop,
  Event,
  h,
  type EventEmitter,
  Element,
  State,
  Method,
} from '@stencil/core';
import { Subject } from 'rxjs';
import type {
  Facet,
  NestedFacet,
} from '../../../../api/model/response/facets/Facets';
import { Strategies, StrategiesFactory } from '../../../strategies/Strategies';
import { PASS_FACET_INFO } from '../../../strategies/utils/variables';
import { PassFacetStrategyService } from '../../../strategies/PassFacetStrategyService';
import { makeLogger } from '../../../../logger/logger';
import type { labelProviderInterface } from '../../../LabelProviderInterface';
import { MiaPomotionBanner } from './banners/mia-promotion-banner';
import { CircularEconomyBanner } from './banners/circular-economy-banner';
import {FACET_CONDITIONS, FACET_MADE_IN_AMERICA} from './variables';
import type {MiaFiltersDetails} from '../../../../components';

const { debug } = makeLogger({
  pathToCurrentFile:
    'components/widgets/filter-related-widget/additional-conditions-banner/additional-conditions-banner.tsx',
  loggersList: ['debug'],
});

const searcherNames = {
  mySe: 'myschneider/products',
  se: 'se/products',
};

@Component({
  tag: 'uiaas-search-widget-additional-conditions-banner',
  styleUrl: 'additional-conditions-banner.scss',
  shadow: true,
})
export class AdditionalConditionsBanner {
  /**
   * label Provider
   */
  @Prop() readonly labelProvider: labelProviderInterface = (n, f) => {
      debug('using fallback label provider, using fallback label for', n);
      return f;
  };

  /**
   * is Product Tab Variant if true - if false - all tab
   */
  @Prop() readonly isProductTabVariant: boolean = false;

  /**
   * term
   */
  @Prop() readonly term: string | undefined;

  /**
   * is Additional Conditions Active - from configuration
   */
  @Prop() readonly isAdditionalConditionsWidgetActive: boolean = false;

  /**
   * is Made in America Promotion Widget - from configuration
   */
  @Prop() readonly isMiaPromotionWidget: boolean = false;

  /**
   * is mySchneider version if true - if false - se.com version
   */
  @Prop() readonly isMySeVersion: boolean = false;

  @Element() el;
  @State() productTabFacets: Array<Facet>;

  @Prop() readonly miaData: MiaFiltersDetails;

  /**
   * Set by strategy
   */
  @State() facetConditionsChildrenNames?: string[];

  private unsubscriber$: Subject<null> = new Subject<null>();

  /**
   * is Filters Selected
   */
  private isFiltersSelected: boolean = false;

  private _strategies: Strategies;

  private isMySe: boolean = this.isMySeVersion;

  // used on Product Tab only - set from response in middleware: reqResCoseBuildQueryAfterEventsWithProductsFacets
  @State() facets: Array<Facet> = [];

  @Method()
  async SetFacets(facets: Array<Facet>) {
      this.facets = facets;
  }

  /**
   * conditions facets for Product Tab
   */
  get conditionsFacetsFromQueryResult(): Facet {
      debug({ facet: this.facets ?? 'not set' });

      const facetId = this.isMiaPromotionWidget ? FACET_MADE_IN_AMERICA : FACET_CONDITIONS;
      return this.facets?.find((f) => f.id == facetId);
  }

  /**
   * is Circular or MiA Data In Product Tab
   */
  get isBannerDataInProductTab(): boolean {
      return this.isProductTabVariant
        ? this.conditionsFacetsFromQueryResult?.fieldValues?.length > 0
        : this.facetConditionsChildrenNames?.length > 0;
  }

  get strategies(): Strategies {
      return this._strategies;
  }

  set strategies(value: Strategies) {
      this._strategies = value;
  }

  private evData = {
    mia: {
      name: 'Made in America',
      category: 'Banner link',
    },
    circular: {
      name: 'Show sustainable conditions',
      category: 'Sustainability Products Banner',
    },
  };

  /**
   * Ga for widget clicked cta
   */
  @Event({
    eventName: 'additional-conditions:ga-cta-banner-clicked',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    gaCtaBannerClickedEventEmitter: EventEmitter<{
    isProductTab: boolean;
    keyword: string;
    ctaLink: string;
    name: string;
    category: string;
  }>;

  /**
   * Ga for widget appearance
   */
  @Event({
    eventName: 'additional-conditions:ga-banner-appearance',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    additionalConditionsBannerAppearanceEventEmitter: EventEmitter<{
    isProductTab: boolean;
    keyword: string;
    category: string;
  }>;

  /**
   * Callback is registered in the strategy, which the strategy will invoke when the observed value changes
   */
  private registerInPassFacetInfoStrategy = (htmlElement: HTMLElement) => {
      this.strategies = StrategiesFactory.createStrategy(htmlElement);
      const passFacetInfo: PassFacetStrategyService =
      this.strategies.for(PASS_FACET_INFO);

      passFacetInfo.register((facetsFromProducts) => {
          this.facetConditionsChildrenNames = facetsFromProducts;
      });
  };

  componentWillLoad() {
      this.registerInPassFacetInfoStrategy(this.el);
  }

  componentWillRender() {
      const isFacetSelected = (key: string): boolean =>
        this.facets?.some(
          (f) =>
            f.id === key && f.fieldValues?.some((fv) => fv.selected === true),
        );

      if (this.isMiaPromotionWidget) {
        this.isFiltersSelected = isFacetSelected(FACET_MADE_IN_AMERICA);
      } else {
        this.isFiltersSelected = isFacetSelected(FACET_CONDITIONS);
      }
  }

  private redirectToProductTab = () => {
      //move to product - from all card header
      const tabName = `[data-tab-name="${searcherNames[this.isMySe ? 'mySe' : 'se']}"]`;

      const searchElement = document.querySelector('uiaas-search')?.shadowRoot;
      const item = searchElement?.querySelector(tabName) as HTMLElement;
      item?.click();

      window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  private bannerInteractionHandler = (evName: string, evCategory: string) => (addFacets: Function, callProducts: Function) => {
      this.gaCtaBannerClickedEventEmitter.emit({
        isProductTab: this.isProductTabVariant,
        keyword: this.term,
        ctaLink: window.location.href,
        name: evName,
        category: evCategory,
      });

      const childrenFacetsForConditions: string[] = [];

      //All tab
      if (!this.isProductTabVariant) {
        this.facetConditionsChildrenNames?.forEach((filterName) => {
            childrenFacetsForConditions.push(filterName);
        });
        this.redirectToProductTab();
      } else if (this.facets) {
        // products tab
        const productFacets = addFacets(childrenFacetsForConditions);
        childrenFacetsForConditions.push(...productFacets);
      }

      const productTab: HTMLUiaasSearchTabElement = this.isMySe
        ? (document.getElementById(
          'search-tab-products-cose',
        ) as HTMLUiaasSearchTabElement)
        : (document.getElementById(
          'search-tab-website-products',
        ) as HTMLUiaasSearchTabElement);

      const filterToSelect = this.isMySe ? 'Produit' : 'Product';
      childrenFacetsForConditions.push(filterToSelect);
    
      callProducts(productTab, childrenFacetsForConditions);
  };

  private circularAddFacets = (childrenFacetsForConditions: string[]) => {
      this.conditionsFacetsFromQueryResult?.fieldValues.forEach(
        (childFacet) => {
            childrenFacetsForConditions.push(childFacet.fieldValue);
        },
      );
      return childrenFacetsForConditions;
  };

  private miaAddFacets = (childrenFacetsForConditions: string[]) => {
      const miaFacets = (this.conditionsFacetsFromQueryResult as NestedFacet)?.children[0];

      miaFacets?.fieldValues.forEach((childFacet) => {
          childrenFacetsForConditions.push(childFacet.fieldValue);
      });
      return childrenFacetsForConditions;
  };

  private circularProductCall = (productTab, childrenFacetsForConditions) => {
      productTab?.setFacetsAndCall(childrenFacetsForConditions);
  };

  private miaProductCall = (productTab, childrenFacetsForConditions) => {
      const nestedFilters = [
        {
          id: 'characteristics_made_in_america',
          value: 'Compliance',
        },
      ];
      productTab?.setFacetsAndCall(childrenFacetsForConditions, nestedFilters);
  };

  /**
   * Logic for display additional conditions banner
   * @param {boolean} isAdditionalConditionsWidgetActive - from configuration, depending on country and language.
   * @param {boolean} isMiaPromotionWidget - from configuration, dedicated for US/EN.
   * @param {boolean} isBannerDataInProductTab - is any circular or MiA data in response.
   * @param {boolean} isProductTabVariant - true = product tab, false = all tab.
   * @param {boolean} isFiltersSelected - applies only product tab - if filters conditions and product are selected - banner should disappear.
   */
  private shouldRenderBanner(
    isBannerEnabled: boolean,
    isBannerDataInProductTab: boolean,
    isProductTabVariant: boolean,
    isFiltersSelected: boolean
  ): boolean {
      return (isBannerEnabled && isBannerDataInProductTab && (!isProductTabVariant || !isFiltersSelected));
  }

  disconnectedCallback() {
      this.unsubscriber$.next(null);
      this.unsubscriber$.complete();
  }

  componentDidLoad() {
      const showMiABanner = this.shouldRenderBanner(
        this.isMiaPromotionWidget,
        this.isProductTabVariant ? this.isBannerDataInProductTab : this.miaData?.miaFilters?.length > 0,
        true,
        this.isFiltersSelected || this.miaData?.isAnyMiaFilterSelected
      );
    
      if(showMiABanner) {
      // emit event once after componant render
        this.additionalConditionsBannerAppearanceEventEmitter.emit({
          isProductTab: this.isProductTabVariant,
          keyword: this.term,
          category: 'widget-cta-made-in-america-products'
        });
      }
  }

  render() {
      const showCircularBanner = this.shouldRenderBanner(
        this.isAdditionalConditionsWidgetActive,
        this.isBannerDataInProductTab,
        this.isProductTabVariant,
        this.isFiltersSelected,
      );

      const showMiABanner = this.shouldRenderBanner(
        this.isMiaPromotionWidget,
        this.isProductTabVariant ? this.isBannerDataInProductTab : this.miaData?.miaFilters?.length > 0,
        true,
        this.isFiltersSelected || this.miaData?.isAnyMiaFilterSelected
      );

      if (!showCircularBanner && !showMiABanner) return '';

      if (showCircularBanner) this.additionalConditionsBannerAppearanceEventEmitter.emit({
        isProductTab: this.isProductTabVariant,
        keyword: this.term,
        category: 'widget-cta-sustainable-products'
      });

      return showMiABanner ? (
        <MiaPomotionBanner
          isProductTabVariant={this.isProductTabVariant}
          labelProvider={this.labelProvider}
          bannerInteractionHandler={() =>
            this.bannerInteractionHandler(
              this.evData.mia.name,
              this.evData.mia.category,
            )(this.miaAddFacets, this.miaProductCall)
          }
        />
      ) : (
        <CircularEconomyBanner
          isProductTabVariant={this.isProductTabVariant}
          labelProvider={this.labelProvider}
          bannerInteractionHandler={() =>
            this.bannerInteractionHandler(
              this.evData.circular.name,
              this.evData.circular.category
            )(this.circularAddFacets, this.circularProductCall)
          }
        />
      );
  }
}
